'use client'

import {observer, useLocalObservable} from 'mobx-react'

import {useStore} from '@/src/shared/hooks/useStore'
import {PageTitle} from '@/src/shared/ui/layout/ui/page-title'
import {LayoutUI} from '@/src/shared/ui/layout/ui/styles'
import {Breadcrumbs} from '@/src/shared/ui/layout/ui/breadcrumbs'
import React, {useState} from 'react'
import {PageType} from '@/src/core/domain/entities/page'
import last from 'lodash/last'
import styled from 'styled-components'
import {Dropdown, Popconfirm} from 'antd'
import {media} from '@/src/config/theme'
import {CreditCard, EllipsisVertical, LogOut, ShoppingCart, Star, UserRound} from 'lucide-react'
import Link from 'next/link'
import {Routes} from '@/src/config/routes'
import sportShoe from '@/src/shared/assets/media/sport-shoe.png'
import {Image} from '@/src/shared/ui/image'
import Button from '@/src/shared/ui/button'
import Modal from '@/src/shared/ui/modal'
import {PersonalPageContent} from '@/src/(pages)/profile'
import {toast} from 'react-toastify'
import {getClient} from '@/src/shared/utils/api-factory'
import {useMutation} from '@tanstack/react-query'
import {isIOS} from '@/src/shared/utils/functions'

export const ProfilePageTitle = observer(() => {
  const {pageData} = useStore<PersonalPageContent>()
  const [openDeleteModal, setOpenDeleteModal] = useState(false)

  const drawer = useLocalObservable(() => ({
    open: false,
    setOpen(open: boolean) {
      this.open = open
    },
  }))

  const title: React.ReactNode =
    (pageData?.type == PageType.ProfileRoot && pageData?.meta?.title) ||
    last(pageData?.breadcrumbs)?.title ||
    pageData?.meta?.title

  const breadcrumbs = pageData?.breadcrumbs ?? []

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const client = getClient()
      await client.post(Routes.api.data.getData, {
        data: {url: Routes.api.profile.delete, id: pageData?.content.user.id},
      })
    },
  })

  const showModal = () => {
    deleteMutation.mutate(undefined, {
      onSuccess() {
        setOpenDeleteModal(true)
      },
      onError(e) {
        toast.error(e.message)
      },
    })
  }

  return (
    <>
      <LayoutUI.Meta>
        {(breadcrumbs?.length > 0 && (
          <UI.Breadcrumbs
            as={Breadcrumbs}
            items={breadcrumbs}
            extra={
              <>
                <UI.Dropdown
                  as={Dropdown}
                  trigger={['click']}
                  onOpenChange={open => drawer.setOpen(open)}
                  open={drawer.open}
                  menu={{
                    items: [
                      {
                        label: (
                          <UI.BreadcrumbLink href={Routes.profile.personal.page}>
                            <UserRound size={18} />
                            <span>Профиль</span>
                          </UI.BreadcrumbLink>
                        ),
                        key: Routes.profile.personal.page,
                      },
                      {
                        label: (
                          <UI.BreadcrumbLink href={Routes.profile.bonus.page}>
                            <CreditCard size={18} />
                            <span>Бонусная карта</span>
                          </UI.BreadcrumbLink>
                        ),
                        key: Routes.profile.bonus.page,
                      },
                      {
                        label: (
                          <UI.BreadcrumbLink href={Routes.profile.orders.page}>
                            <ShoppingCart size={18} />
                            <span>Мои заказы</span>
                          </UI.BreadcrumbLink>
                        ),
                        key: Routes.profile.orders.page,
                      },
                      {
                        label: (
                          <UI.BreadcrumbLink href={Routes.profile.service.records.page}>
                            <Image src={sportShoe} alt="" width={18} height={18} />
                            <span>Запись на сервисы</span>
                          </UI.BreadcrumbLink>
                        ),
                        key: Routes.profile.service.records.page,
                      },
                      {
                        label: (
                          <UI.BreadcrumbLink href={Routes.profile.favourite.page}>
                            <Star size={18} />
                            <span>Избранное</span>
                          </UI.BreadcrumbLink>
                        ),
                        key: Routes.profile.favourite.page,
                      },
                      {
                        label: (
                          <UI.BreadcrumbLink href={Routes.auth.logout.page}>
                            <LogOut size={18} />
                            <span>Выйти</span>
                          </UI.BreadcrumbLink>
                        ),
                        key: Routes.auth.logout.page,
                      },
                    ],
                  }}>
                  <EllipsisVertical />
                </UI.Dropdown>

                {/*<Drawer*/}
                {/*  title="Basic Drawer"*/}
                {/*  closable={{'aria-label': 'Close Button'}}*/}
                {/*  onClose={() => drawer.setOpen(false)}*/}
                {/*  open={drawer.open}>*/}
                {/*  <p>Some contents...</p>*/}
                {/*  <p>Some contents...</p>*/}
                {/*  <p>Some contents...</p>*/}
                {/*</Drawer>*/}
              </>
            }
          />
        )) ||
          null}

        <PageTitle>
          <h1>{title}</h1>

          {isIOS() && (
            <Popconfirm
              title="Вы точно уверены?"
              okText="Да"
              cancelText="Нет"
              onConfirm={showModal}>
              <Button loading={deleteMutation.isPending}>Удалить аккаунт</Button>
            </Popconfirm>
          )}
        </PageTitle>
      </LayoutUI.Meta>

      <Modal open={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
        <p style={{fontSize: 26}}>Ваш аккаунт будет удален в течение 24 часов</p>
      </Modal>
    </>
  )
})

const UI = {
  Breadcrumbs: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  Dropdown: styled.div`
    display: flex;

    @media ${media.lg} {
      display: none;
    }
  `,
  BreadcrumbLink: styled(Link)`
    display: flex;
    align-items: center;
    gap: 10px;

    span {
      font-size: 18px;
    }
  `,
}
